import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import {
  BannerCloseButton,
  BannerCta,
  BannerIconTile,
  BannerImage,
  BannerText,
} from "./BannerParts";
import {
  BANNER_VARIANTS,
  bannerSurfaceSx,
  type BannerVisual,
  type InlineBannerVariant,
} from "./bannerVariants";

type InlineBannerProps = {
  title: string;
  text?: string;
  ctaLabel: string;
  onCtaClick: () => void;
  onClose?: () => void;
  variant: InlineBannerVariant;
  visual?: BannerVisual;
};

/** Card between page sections, scrolling with the content. */
export const InlineBanner = (props: InlineBannerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={bannerSurfaceSx(props.variant, { borderless: true })}
      data-testid="inline-banner"
    >
      {isMobile ? <InlineMobile {...props} /> : <InlineDesktop {...props} />}
    </Box>
  );
};

const InlineDesktop = ({
  title,
  text,
  ctaLabel,
  onCtaClick,
  onClose,
  variant,
  visual,
}: InlineBannerProps) => {
  const textBlock = (
    <BannerText title={title} text={text} variant={variant} size="medium" />
  );
  const cta = (
    <BannerCta label={ctaLabel} onClick={onCtaClick} variant={variant} />
  );
  const close = onClose && (
    <BannerCloseButton onClose={onClose} variant={variant} />
  );

  if (visual?.type === "image") {
    return (
      <Stack direction="row" alignItems="flex-start" spacing={3} p={3}>
        <BannerImage
          src={visual.src}
          alt={visual.alt}
          width={200}
          height={112}
        />
        <Stack flex={1} minWidth={0} alignItems="flex-start" spacing={3}>
          {textBlock}
          {cta}
        </Stack>
        {close}
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={3} p={3}>
      {visual && (
        <BannerIconTile
          icon={visual.icon}
          tone={BANNER_VARIANTS[variant].iconTone}
          size="medium"
        />
      )}
      {textBlock}
      {cta}
      {close}
    </Stack>
  );
};

const InlineMobile = ({
  title,
  text,
  ctaLabel,
  onCtaClick,
  onClose,
  variant,
  visual,
}: InlineBannerProps) => (
  <Stack spacing={2.5} pl={3} pr={2.5} py={3}>
    {visual?.type === "image" && (
      <BannerImage
        src={visual.src}
        alt={visual.alt}
        width="100%"
        height={120}
      />
    )}
    <Stack direction="row" alignItems="flex-start" spacing={2.5}>
      {visual?.type === "icon" && (
        <BannerIconTile
          icon={visual.icon}
          tone={BANNER_VARIANTS[variant].iconTone}
          size="medium"
        />
      )}
      <BannerText title={title} text={text} variant={variant} size="medium" />
      {onClose && <BannerCloseButton onClose={onClose} variant={variant} />}
    </Stack>
    <BannerCta
      label={ctaLabel}
      onClick={onCtaClick}
      variant={variant}
      fullWidth
    />
  </Stack>
);
